import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { KDateAdd, KDateFormat, KDateIs, KDateParse, KDateStartOf } from '@kiwid-app/kiwid-date-typescript';
import { ITableReorder, KTableComponent, ModalComponent, ToastrService } from '@kiwid-app/kiwid-ui-lib-angular';
import { TranslateService } from '@ngx-translate/core';
import { DATE_API, DATE_TIME_API } from 'app/core/const/date.const';
import { REQUEST_LIMIT } from 'app/core/const/pagination.const';
import { FileHandle } from 'app/core/directive';
import { EMediaType } from 'app/core/enums/media-type.enum';
import { ConvertHelper } from 'app/core/helpers/convert.helper';
import { FileHelper } from 'app/core/helpers/file.helper';
import { Util, parseStringToSearch } from 'app/core/helpers/util';
import { IDataPagination, IMeta } from 'app/core/models/api/data';
import { IPagination, IParamPagination } from 'app/core/models/api/pagination';
import { IError } from 'app/core/models/error/error';
import { MoneyService } from 'app/core/services/money/money.service';
import { TimezoneService } from 'app/core/services/timezone/timezone.service';
import { FuseTranslationLoaderService, Locale } from 'app/core/translate/translation-loader.service';
import { environment } from 'environments/environment';
import moment, { Moment } from 'moment/moment';
import { Observable, Subscription, forkJoin, last, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable()
export abstract class BaseComponent {
    protected readonly baseFuseTranslationLoaderService = inject(FuseTranslationLoaderService);
    protected readonly baseTranslateService = inject(TranslateService);
    private readonly baseTitleFuse = inject(Title);
    private readonly baseRouter = inject(Router);
    private readonly baseHttpClient = inject(HttpClient);
    private readonly baseIconRegistry = inject(MatIconRegistry);
    private readonly baseDomSanitizer = inject(DomSanitizer);

    protected readonly subscriptions = new Subscription();
    protected translate: any;
    protected readonly changeLanguage = new EventEmitter();

    constructor() {
        this.subscriptions.add(
            this.baseTranslateService.onLangChange.subscribe(() => {
                this.changeLanguage.emit();
            }),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    isNumber(value: any): boolean {
        return typeof value === 'number';
    }

    parseStringToSearch(search: string): string {
        return parseStringToSearch(search);
    }

    checkFilesDropped(
        files: FileHandle[],
        options?: {
            mediasType?: EMediaType[];
            maxFiles?: {
                quantity: number;
                message: string;
                toastr: ToastrService | ModalComponent;
            };
        },
    ): FileHandle[] {
        if (!!options) {
            if (options.maxFiles && files.length > options.maxFiles.quantity) {
                const toastr = options.maxFiles.toastr;
                const message = options.maxFiles.message;

                if (toastr instanceof ModalComponent) {
                    toastr?.showToastr({ message, type: 'error' });
                } else {
                    toastr?.show({ message, type: 'error' });
                }

                return [];
            }
        }

        return files.filter((file) => {
            if (!!options) {
                if (options.mediasType?.length > 0 && !options.mediasType?.includes(FileHelper.getMediaTypeByName(file.file.name))) {
                    return false;
                }
            }

            return true;
        });
    }

    protected useLanguage(lang: string): void {
        this.baseTranslateService.use(lang);
        this.baseTranslateService.reloadLang(lang);
        this.changeLanguage.emit();
    }

    protected convertStringToNumber(value: string): number {
        return Number(value);
    }

    protected convertToMaskMoney(
        moneyService: MoneyService,
        value: string | number,
        option: { decimalSeparator: string; prefix: string; thousandSeparator: string; suffix?: string } = {
            prefix: `${moneyService.maskMoney.symbol} `,
            thousandSeparator: moneyService.maskMoney.thousandSeparator,
            decimalSeparator: moneyService.maskMoney.decimalSeparator,
        },
        fractionDigits = 2,
    ): string {
        return ConvertHelper.convertToMaskMoney(value, option, fractionDigits);
    }

    protected registerOnTranslate(key: string, portuguese: Locale, english: Locale, spanish: Locale, enableTitle = true): void {
        this.baseFuseTranslationLoaderService.loadTranslations(portuguese, english, spanish);
        this.subscriptions.add(
            this.changeLanguage.subscribe(() => {
                this.registerOnTranslate(key, portuguese, english, spanish, enableTitle);
            }),
        );

        const subscription = this.baseTranslateService.get(key).subscribe((response: any) => {
            this.translate = response;
            if (enableTitle) {
                this.baseTitleFuse.setTitle(`${environment.titleBase} - ${this.translate?.TITLE}`);
            }
        });

        this.subscriptions.add(subscription);
    }

    protected setTitle(): void {
        this.baseTitleFuse.setTitle(`${environment.titleBase} - ${this.translate?.TITLE ?? ''}`);
    }

    protected changeTitle(title: string): void {
        this.baseTitleFuse.setTitle(`${environment.titleBase} - ${title}`);
    }

    protected registerIcons(icons: IIconRegistry[]): void {
        for (const { iconName, url } of icons) {
            this.baseIconRegistry.addSvgIcon(iconName, this.baseDomSanitizer.bypassSecurityTrustResourceUrl(url));
        }
    }

    protected updateQueryParam(router: Router, queryParams: any): void {
        router
            .navigate([], {
                queryParams,
                queryParamsHandling: 'merge',
            })
            .then(() => {
                // console.log('foi?');
            });
    }

    formatDate(date: Date, format: string = DATE_API): string {
        return KDateFormat(date, format, { lang: Util.currentLang() });
    }

    parseDate(date: string, format = DATE_API): Date {
        return KDateParse({ value: date, format });
    }

    parseDateWithTime(date: string): Date {
        return new Date(date);
    }

    changeValueFormBuilder(form: AbstractControl, value: any): void {
        form.setValue(value);
    }

    parseQueryParams<Interface>(activatedRoute: ActivatedRoute, defaultData: Interface, cropExtraData = true): Interface {
        return Util.parseQueryParams(activatedRoute, defaultData, cropExtraData);
    }

    translateKey(key: string, params?: { [key: string]: string }, type?: 'one' | 'other' | 'zero'): string {
        return Util.translateKey(this.translate, key, params, type);
    }

    getFormControlByKey(formGroup: AbstractControl | FormGroup, key: string): FormControl {
        return formGroup.get(key) as FormControl;
    }

    setDate(event: MatDatepickerInputEvent<any>, form: AbstractControl, format: string): void {
        form.setValue(KDateFormat(event.value.toDate(), format));
    }

    setOpen(event: MatDatepicker<any>, form: AbstractControl, format: string): void {
        if (KDateIs({ value: form.value, format }, 'valid')) {
            event.select(moment(KDateParse({ value: form.value, format }, new Date())));
        }
    }

    getDateFormatted(date: Date, format: string): string {
        return KDateFormat(date, format, { lang: Util.currentLang() });
    }

    protected sortParameters<I>(parameters: I, events: ITableReorder | ITableReorder[]): I {
        events = Array.isArray(events) ? events : [events];
        for (const [key] of Object.entries(parameters)) {
            if (key.indexOf('orderBy[') === 0) {
                // delete parameters[key];
                parameters[key] = null;
            }
        }

        for (const event of events) {
            if (event.type === 'sort') {
                if (event.order === 'NONE') {
                    continue;
                }

                parameters = {
                    ...parameters,
                    [`orderBy[${event.sort}]`]: event.order,
                };
            }
        }

        return parameters;
    }

    protected setSortTable<I>(table: KTableComponent<I>, parameters: I): void {
        if (!table) {
            return;
        }
        for (const [key, value] of Object.entries(parameters)) {
            if (key.indexOf('orderBy[') === 0 && !!value) {
                table.setSort(value, key.replaceAll('orderBy[', '').replaceAll(']', ''));
                return;
            }
        }
    }

    sortAndRequestItems<T, R>(
        key: string,
        keysAdd: string[],
        items: R[],
        sortFnc: (a: R, b: R) => number,
        parseResult: (item: T) => R,
        url: string[],
        queryParams: any,
        isLastPage = false,
    ): Observable<R[]> {
        return new Observable((observer) => {
            items.sort(sortFnc);

            if (isLastPage) {
                observer.next(items);
                observer.complete();
                return;
            }

            let quantity = 0;
            keysAdd.forEach((value) => {
                if (value === items.at(-1)?.[key]) {
                    items.splice(items.length - 1, 1);
                    quantity++;
                }
            });

            if (quantity === 0) {
                observer.next(items);
                observer.complete();
                return;
            }

            const urlTree = this.baseRouter.createUrlTree(url, {
                queryParams: {
                    ...queryParams,
                    offset: items.length,
                    limit: quantity,
                },
            });
            const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${urlTree}`;
            this.subscriptions.add(
                this.baseHttpClient
                    .get(parsedUrl, { responseType: 'json' })
                    .pipe(
                        map((res: IDataPagination<T[]>) => {
                            return res;
                        }),
                        catchError((error: HttpErrorResponse) => {
                            if (error.error instanceof ErrorEvent) {
                                return throwError(() => `Error: ${error.error.message}`);
                            }
                            const errorApi: IError = error.error.error;
                            return throwError(() => errorApi);
                        }),
                    )
                    .subscribe({
                        next: ({ data }) => {
                            items.push(...data.map((item) => parseResult(item)));
                            observer.next(items);
                            observer.complete();
                        },
                        error: (error) => {
                            observer.error(error);
                            observer.complete();
                        },
                    }),
            );
        });
    }

    protected consumeRequestUrl<T>(
        router: Router,
        httpClient: HttpClient,
        url: string[],
        queryParams: any & IParamPagination,
    ): Observable<IDataPagination<T[]>> {
        return new Observable<IDataPagination<T[]>>((observer) => {
            // tslint:disable-next-line:no-shadowed-variable
            const generateRequest = (offset: number, limit: number) => {
                const urlTree = router.createUrlTree(url, {
                    queryParams: {
                        ...queryParams,
                        offset,
                        limit,
                    },
                });
                const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${urlTree}`;
                return httpClient.get(parsedUrl, { responseType: 'json' }).pipe(
                    map((res: IDataPagination<T[]>) => {
                        return res;
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error.error instanceof ErrorEvent) {
                            return throwError(() => `Error: ${error.error.message}`);
                        }
                        const errorApi: IError = error.error.error;
                        return throwError(() => errorApi);
                    }),
                );
            };
            const totalOffSet = queryParams.offset;

            if (totalOffSet < 0) {
                observer.error({ message: 'totalOffSet cannot be less than zero' });
                observer.complete();
                return;
            }

            const observers: Observable<IDataPagination<T[]>>[] = [];
            const limit = REQUEST_LIMIT;
            let offset = 0;
            while (offset < totalOffSet) {
                observers.push(generateRequest(offset, offset + limit > totalOffSet ? totalOffSet - offset : limit));
                offset += limit;
            }

            this.subscriptions.add(
                forkJoin(observers)
                    .pipe(last())
                    .subscribe({
                        next: (res) => {
                            const data: T[] = [];
                            let meta: IMeta<IPagination>;
                            const lastIndex = res.length > 0 ? res.length - 1 : 0;

                            res.forEach((item, index) => {
                                data.push(...item.data);

                                if (lastIndex === index) {
                                    meta = {
                                        pagination: {
                                            offset: 0,
                                            limit: totalOffSet,
                                            count: totalOffSet,
                                            total: item.meta.pagination.total,
                                        },
                                    };
                                }
                            });

                            observer.next({
                                data,
                                meta,
                            });

                            observer.complete();
                        },
                        error: (error) => {
                            observer.error(error);
                            observer.complete();
                        },
                    }),
            );
        });
    }
}

export interface IIconRegistry {
    iconName: string;
    url: string;
}
